// Plano explicável — gera "chips de regra" que dizem POR QUE cada item entrou no
// plano de compra e como o motor o priorizou. Função pura e determinística: usa
// só os campos que o `gerarPlanoDeCompra` já produz (prioridade, em_risco, curva,
// saldo, ×2 de ruptura, teto). O objetivo é tornar a regra visível/auditável —
// o Miguel não decide nada aqui, apenas descreve a decisão do motor.

export type ChipTone = 'ruptura' | 'risco' | 'baixo' | 'comprar' | 'abc' | 'dobra' | 'virada' | 'info'

export interface RuleChip {
  /** Rótulo curto do chip (ex.: "Zerado", "×2 anti-ruptura", "Curva A"). */
  label: string
  tone: ChipTone
  /** Frase explicativa (title/tooltip) — o "porquê" completo. */
  why: string
}

export interface PlanExplainInput {
  priority: string
  em_risco: boolean
  abc_curve: 'A' | 'B' | 'C' | null
  current_stock: number
  /** Item entra dobrado (ruptura acionável)? Vem de isRupturaAcionavel no chamador. */
  dobrado: boolean
  /** True quando o item caiu no cap rolante (próximos meses / não cabe no teto). */
  virada?: boolean
}

const PRIORITY_CHIP: Record<string, { label: string; tone: ChipTone; why: string }> = {
  Zerado:  { label: 'Zerado',  tone: 'ruptura', why: 'Saldo zerado — está em ruptura; comprar é urgente para não perder venda.' },
  Baixo:   { label: 'Baixo',   tone: 'baixo',   why: 'Cobertura abaixo do colchão — vai zerar dentro do lead time se não repor.' },
  Comprar: { label: 'Comprar', tone: 'comprar', why: 'Cruzou o ponto de ressuprimento (lead time + colchão) — hora de comprar.' },
}

/**
 * Chips que explicam a entrada e a prioridade de um item no plano. Ordem = leitura
 * natural: motivo de entrada → reforço de ruptura → curva ABC → destino (virada).
 * O primeiro chip é sempre o "porquê entrou" (prioridade ou risco).
 */
export function explainPlanoItem(i: PlanExplainInput): RuleChip[] {
  const chips: RuleChip[] = []

  // 1. Por que entrou no plano.
  const pri = PRIORITY_CHIP[i.priority]
  if (pri) {
    chips.push(pri)
  } else if (i.em_risco) {
    chips.push({
      label: 'Em risco',
      tone: 'risco',
      why: 'Ainda não zerou, mas cai rápido dentro do lead time — entra antes para prevenir a ruptura.',
    })
  } else {
    // Fallback defensivo: item no plano sem prioridade-alvo nem risco (não deveria ocorrer).
    chips.push({ label: 'No plano', tone: 'info', why: 'Selecionado pelo motor de reposição.' })
  }

  // 2. Reforço anti-ruptura (quantidade dobrada).
  if (i.dobrado) {
    chips.push({
      label: '×2 anti-ruptura',
      tone: 'dobra',
      why: 'Item em ruptura entra com o dobro da sugestão — a quantidade simples re-rompe dentro do lead time.',
    })
  }

  // 3. Curva ABC (peso comercial que ordena a alocação sob o teto).
  if (i.abc_curve) {
    chips.push({
      label: `Curva ${i.abc_curve}`,
      tone: 'abc',
      why: i.abc_curve === 'A'
        ? 'Curva A — maior giro/importância; tem prioridade na alocação do teto.'
        : i.abc_curve === 'B'
          ? 'Curva B — importância média na alocação do teto.'
          : 'Curva C — cauda longa; cede espaço no teto para A e B.',
    })
  }

  // 4. Destino: caiu para a virada (cap rolante).
  if (i.virada) {
    chips.push({
      label: 'Vai p/ virada',
      tone: 'virada',
      why: 'Não coube no teto deste mês — alocado nos próximos meses (cap rolante) ou pede revisão do teto.',
    })
  }

  return chips
}

/** Cores dos chips (dual-theme), alinhadas à paleta de prioridade/semáforo. */
export const CHIP_TONE_CLASS: Record<ChipTone, string> = {
  ruptura: 'bg-[#7030A0]/15 text-[#7030A0] dark:text-purple-300 border border-[#7030A0]/30',
  risco:   'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300 border border-rose-500/20',
  baixo:   'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300 border border-red-500/20',
  comprar: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300 border border-sky-500/20',
  abc:     'bg-sunken text-ink-muted border border-line',
  dobra:   'bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/30',
  virada:  'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 border border-amber-500/20',
  info:    'bg-sunken text-ink-muted border border-line',
}
